// MyGiftCardsPage.jsx
import { useState, useEffect } from 'react';
import { Gift, Mail, Calendar, DollarSign, Eye, X } from 'lucide-react';
import { summaryApi } from '../../common';
import { useTheme } from "../../components/Theme/ThemeProvider";
import { authFetch } from '../../helpers/authFetch';

export function MyGiftCardsPage() {
  const [giftCards, setGiftCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedCard, setSelectedCard] = useState(null);
  const { colors } = useTheme();

  useEffect(() => {
    fetchMyGiftCards();
  }, []);

  const fetchMyGiftCards = async () => {
    try {
      const response = await authFetch(summaryApi.getMyGiftCards.url, {
        method: 'GET',
      
      headers: {
        'Content-Type': 'application/json'
      }
      });
      const data = await response.json();

      if (data.success) {
        setGiftCards(data.data);
      } else {
        setError(data.message);
      }
    } catch (err) {
      setError('Failed to load gift cards');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return { bg: `${colors.success}20`, text: colors.success };
      case 'redeemed': return { bg: `${colors.textSecondary}20`, text: colors.textSecondary };
      case 'expired': return { bg: `${colors.error}20`, text: colors.error };
      case 'cancelled': return { bg: `${colors.warning}20`, text: colors.warning };
      default: return { bg: `${colors.textSecondary}20`, text: colors.textSecondary };
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: colors.bgPrimary }}>
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-t-transparent rounded-full animate-spin mx-auto mb-4" 
            style={{ borderColor: colors.primary, borderTopColor: 'transparent' }} />
          <p style={{ color: colors.textSecondary }}>Loading your gift cards...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12 px-4" style={{ backgroundColor: colors.bgPrimary }}>
      <div className="max-w-6xl mx-auto">
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-2" style={{ color: colors.text }}>My Gift Cards</h1>
          <p style={{ color: colors.textSecondary }}>Gift cards you've purchased</p>
        </div>

        {error && (
          <div className="border rounded-lg p-4 mb-6" style={{
            backgroundColor: `${colors.error}10`,
            borderColor: colors.error
          }}>
            <p style={{ color: colors.error }}>{error}</p>
          </div>
        )}

        {giftCards.length === 0 ? (
          <div className="rounded-lg shadow-lg p-12 text-center" style={{ backgroundColor: colors.bgSecondary }}>
            <Gift className="w-16 h-16 mx-auto mb-4" style={{ color: colors.textSecondary }} />
            <h3 className="text-xl font-semibold mb-2" style={{ color: colors.text }}>No Gift Cards Yet</h3>
            <p className="mb-6" style={{ color: colors.textSecondary }}>You haven't purchased any gift cards</p>
            <button
              onClick={() => window.location.href = '/purchase-giftcard'}
              className="px-6 py-3 rounded-lg text-white transition"
              style={{ backgroundColor: colors.primary }}
            >
              Purchase Gift Card
            </button>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-6">
            {giftCards.map((card) => (
              <div key={card._id} className="rounded-lg shadow-lg overflow-hidden" style={{ backgroundColor: colors.bgSecondary }}>
                <div className="p-6 text-white" style={{
                  background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.accent} 100%)`
                }}>
                  <div className="flex justify-between items-start mb-4">
                    <Gift className="w-8 h-8" />
                    <span className="px-3 py-1 rounded-full text-xs font-medium" style={{
                      backgroundColor: getStatusColor(card.status).bg,
                      color: getStatusColor(card.status).text
                    }}>
                      {card.status}
                    </span>
                  </div>
                  <p className="text-3xl font-bold mb-2">₦{card.currentBalance.toLocaleString()}</p>
                  <p className="text-sm opacity-75">of ₦{card.originalAmount.toLocaleString()}</p>
                </div>

                <div className="p-6 space-y-3">
                  <div className="flex items-center gap-2 text-sm">
                    <Mail className="w-4 h-4" style={{ color: colors.textSecondary }} />
                    <span style={{ color: colors.textSecondary }}>To:</span>
                    <span className="font-medium" style={{ color: colors.text }}>{card.recipientEmail}</span>
                  </div>

                  <div className="flex items-center gap-2 text-sm">
                    <Calendar className="w-4 h-4" style={{ color: colors.textSecondary }} />
                    <span style={{ color: colors.textSecondary }}>Sent:</span>
                    <span className="font-medium" style={{ color: colors.text }}>
                      {new Date(card.sentDate).toLocaleDateString()}
                    </span>
                  </div>

                  <div className="flex items-center gap-2 text-sm">
                    <Calendar className="w-4 h-4" style={{ color: colors.textSecondary }} />
                    <span style={{ color: colors.textSecondary }}>Expires:</span>
                    <span className="font-medium" style={{ color: colors.text }}>
                      {new Date(card.expiryDate).toLocaleDateString()}
                    </span>
                  </div>

                  {card.message && (
                    <div className="rounded-lg p-3 mt-4" style={{ backgroundColor: colors.bgTertiary }}>
                      <p className="text-sm italic" style={{ color: colors.textSecondary }}>"{card.message}"</p>
                    </div>
                  )}

                  <button
                    onClick={() => setSelectedCard(card)}
                    className="w-full mt-4 px-4 py-2 border rounded-lg transition flex items-center justify-center gap-2"
                    style={{
                      borderColor: colors.border,
                      color: colors.text,
                      backgroundColor: colors.bgPrimary
                    }}
                  >
                    <Eye className="w-4 h-4" />
                    View Details
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {selectedCard && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="rounded-lg max-w-md w-full p-6" style={{ backgroundColor: colors.bgSecondary }}>
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-xl font-bold" style={{ color: colors.text }}>Gift Card Details</h3>
                <button
                  onClick={() => setSelectedCard(null)}
                  style={{ color: colors.textSecondary }}
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div className="rounded-lg p-4" style={{ backgroundColor: colors.bgTertiary }}>
                  <p className="text-sm mb-1" style={{ color: colors.textSecondary }}>Gift Card Code</p>
                  <p className="text-lg font-mono font-bold" style={{ color: colors.primary }}>
                    {selectedCard.code}
                  </p>
                </div>

                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span style={{ color: colors.textSecondary }}>Original Amount:</span>
                    <span className="font-medium" style={{ color: colors.text }}>
                      ₦{selectedCard.originalAmount.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span style={{ color: colors.textSecondary }}>Current Balance:</span>
                    <span className="font-medium" style={{ color: colors.text }}>
                      ₦{selectedCard.currentBalance.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span style={{ color: colors.textSecondary }}>Status:</span>
                    <span className="px-2 py-1 rounded text-xs font-medium" style={{
                      backgroundColor: getStatusColor(selectedCard.status).bg,
                      color: getStatusColor(selectedCard.status).text
                    }}>
                      {selectedCard.status}
                    </span>
                  </div>
                </div>

                {selectedCard.transactions && selectedCard.transactions.length > 0 && (
                  <div>
                    <p className="text-sm font-medium mb-2" style={{ color: colors.text }}>
                      Transaction History
                    </p>
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {selectedCard.transactions.map((txn, idx) => (
                        <div key={idx} className="text-sm flex justify-between py-2 border-b" style={{ borderColor: colors.border }}>
                          <span className="capitalize" style={{ color: colors.textSecondary }}>{txn.type}</span>
                          <span className="font-medium" style={{ color: colors.text }}>
                            ₦{txn.amount.toLocaleString()}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}